import Mexp from 'math-expression-evaluator';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';
import { compareObjects } from '../../../functions/utils/comapareObjects';
import { extractNumber } from '../../../functions/utils/extractNumber';
import { generateNumberString } from '../../../functions/utils/generateNumberString';
import { getNumberInRange } from '../../../functions/utils/getValueInRange';
import { parseNumberString } from '../../../functions/utils/parseNumberString';
import { useMouseStore } from '../../../hooks/stores/mouseStore';
import { DropdownInput } from './DropdownInput';

const mexp = new Mexp();

export const NumberInput = ({
  label,
  iconClassName,
  value,
  placeholder,
  onChange,
  id,
  tooltip,

  precision,
  units,
  defaultUnit,
  minValue,
  maxValue,
  draggingStep,
  draggingSensitive,

  percentMin = 0,
  percentMax = 100,

  style,
  inputStyle,
  isDisabled,
}: {
  label?: string;
  iconClassName?: string;
  value?: string | number | null;
  placeholder?: string;
  onChange: (newValue?: string | number) => void;
  id?: string;
  tooltip?: string;

  precision?: number;
  units?: string[];
  defaultUnit?: string;
  minValue?: number;
  maxValue?: number;
  draggingStep?: number;
  draggingSensitive?: number;

  percentMin?: number;
  percentMax?: number;

  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  isDisabled?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [mouseData] = useMouseStore();
  const [isDragging, setIsDragging] = useState(false);
  const [startValue, setStartValue] = useState<number>(0);

  const data = useMemo(() => value === null ? {
    number: null,
    unit: null,
  } : parseNumberString({ value, supportedUnits: units }), [value]);

  const getNumberValueInRange = (value?: number | null, unit?: string | null) => {
    if (value === undefined || value === null) return value;

    return getNumberInRange({
      value,
      min: (unit || data.unit) === '%' && percentMin !== undefined ? percentMin : minValue,
      max: (unit || data.unit) === '%' && percentMax !== undefined ? percentMax : maxValue,
    });
  };

  const updateData = (newData: { number?: number | null; unit?: string | null } | undefined) => {
    if (newData === undefined || newData.number === undefined || newData.number === null) {
      onChange(undefined);
      return;
    }

    let numberValue = getNumberValueInRange(newData.number, newData.unit);
    if (numberValue !== undefined && numberValue !== null) {
      if (precision !== undefined) numberValue = Number(numberValue.toFixed(precision));
    }

    const numberString = generateNumberString({
      number: numberValue!,
      unit: newData.unit || defaultUnit || undefined,
    });

    onChange(numberString);
    // to update input value even if state didn't change
    if (ref.current) ref.current.value = data.number === undefined || data.number === null ? '' : String(data.number);
  };

  // Sync input value with data
  useEffect(() => {
    if (!ref.current) return;

    ref.current.value = data.number === undefined || data.number === null ? '' : String(data.number);
  }, [data]);

  // Dragging update value
  useEffect(() => {
    if (!ref.current || !mouseData.draggingDiff || !isDragging) return;
    const xDiff = mouseData.draggingDiff.x;
    const newValue = startValue + Math.round(xDiff * (draggingSensitive || 0.5)) * (draggingStep || 1);

    updateData({ number: newValue, unit: data.unit });
  }, [isDragging, mouseData.draggingDiff]);

  // Dragging reset
  useEffect(() => {
    if (!mouseData.isDown) {
      setIsDragging(false);
    }
  }, [mouseData.isDown]);

  return (
    <Container $isDragging={isDragging} style={style} $isDisabled={isDisabled}>
      <DraggingHandle
        // Dragging init
        onMouseDown={() => {
          const numberValueInRange = getNumberValueInRange(data.number || 0);
          setIsDragging(true);
          setStartValue(Number(numberValueInRange));
        }}
      />

      {(label || iconClassName) && (
        <Label
          id={id ? `${id}-label` : undefined}
          onClick={() => ref.current?.focus()}
          // Dragging init
          onMouseDown={() => {
            const numberValueInRange = getNumberValueInRange(data.number || 0);
            setIsDragging(true);
            setStartValue(Number(numberValueInRange));
          }}
        >
          {iconClassName && <i className={iconClassName} />}
          {label}
        </Label>
      )}
      {tooltip && id && <Tooltip anchorSelect={`#${id}-label`} place="bottom" content={tooltip} delayShow={1000} />}

      <InputField
        ref={ref}
        type="text"
        style={inputStyle}
        placeholder={value === undefined ? placeholder || 'auto' : value === null ? 'mixed' : undefined}
        onFocus={(e) => e.target?.select()}
        onChange={(e) => {
          const inputValue = e.target.value;
          if (inputValue === '') {
            updateData(undefined);
            return;
          }
        }}
        onBlur={(e) => {
          const inputValue = e.target.value;

          const isNumber = Number(inputValue) === extractNumber(inputValue);
          if (isNumber) {
            updateData({
              number: Number(inputValue),
              unit: data.unit,
            });
          } else {
            try {
              const mathValue = mexp.eval(inputValue);
              mathValue !== undefined &&
                updateData({
                  number: Number(mathValue),
                  unit: data.unit,
                });
            } catch {
              const newData = parseNumberString({
                value: inputValue,
                supportedUnits: units,
              });

              if (!compareObjects(newData, data)) {
                updateData(newData);
                return;
              }
            }
            if (ref.current) ref.current.value = data.number === undefined ? '' : String(data.number);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') ref.current?.blur();

          const mathOperators = ['+', '-', '*', '/'];
          if (mathOperators.includes(e.key)) {
            e.preventDefault();
            e.currentTarget.value += e.key;
          }
        }}
        spellCheck={false}
        $isComputed={value === undefined && placeholder !== undefined}
      />

      {units && (
        <DropdownInput
          value={data.unit || defaultUnit || units[0]}
          onChange={(unit) => {
            updateData({
              number: data.number,
              unit: String(unit),
            });
          }}
          options={units?.map((unit) => ({ value: unit, label: unit })) || []}
          canBeCleared={false}
          isSearchable={false}
          isArrowVisible={false}
          textColor="var(--main-2)"
          textAlign="center"
          style={{
            maxWidth: '25px',
            outline: units ? undefined : 'none',
            padding: 0,
          }}
          horizontalAlign="center"
          isDisabled={units.length === 1}
        />
      )}
    </Container>
  );
};

const Container = styled.div<{ $isDragging?: boolean; $isDisabled?: boolean }>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 2px;
  /* padding: 0 8px; */

  border-radius: var(--radius-2);

  &:hover {
    outline: 1px solid var(--neutral-3);
    ${({ $isDragging }) =>
    $isDragging &&
    `
      outline: 1px solid var(--primary-1);
    `}
  }

  &:focus-within {
    outline: 1px solid var(--primary-1);
  }

  ${({ $isDragging }) =>
    $isDragging &&
    `
    outline: 1px solid var(--primary-1);
  `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    pointer-events: none;
    opacity: 0.5;
  `}
`;

const DraggingHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 5px;
  height: 100%;
  background-color: transparent;
  cursor: ew-resize;
`;

const Label = styled.label`
  cursor: ew-resize;
  padding-right: 5px;
  padding-left: 8px;

  color: var(--main-2);
  font-family: var(--font-2);
  font-size: 13px;
  white-space: nowrap;

  display: flex;
  align-items: center;

  i {
    width: 20px;
    font-size: 18px;
    translate: -2px 0;
  }
`;

const InputField = styled.input<{ $isComputed?: boolean }>`
  cursor: default;
  box-sizing: border-box;
  width: 100%;
  height: 25px;

  overflow: hidden;
  text-overflow: ellipsis;

  background-color: transparent;
  border: none;

  color: var(--main-1);
  font-family: var(--font-1);
  font-size: 11px;
  font-weight: 300;

  &:focus {
    outline: none;
  }

  &::selection {
    background: var(--primary-2);
    color: var(--main-1);
  }

  ${({ $isComputed }) =>
    $isComputed &&
    `
    color: var(--main-2);
    font-style: italic;
  `}
`;
